import { SettingOutlined } from '@ant-design/icons'
import { Col, message, Popconfirm, Popover, Row } from 'antd'
import { withFormik } from 'formik'
import { isEmpty } from 'lodash'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import * as Yup from 'yup'
import {
  addSalaryHistoryDetails,
  getSalaryHistoryDetailsByEmployeeID,
  updateSalaryHistoryDetailsByEmployeeID
} from '../../../Actions/EmployeeActions'
import ButtonBox from '../../../Components/ButtonBox/ButtonBox'
import FooterActions from '../../../Components/FooterActions'
import Form from '../../../Components/Formik/Form'
import ModalBox from '../../../Components/ModalBox/ModalBox'
import TableBox from '../../../Components/TableBox/TableBox'
import Panel from '../../../Layout/Panel'
import PanelLayout from '../../../Layout/PanelLayout'
import SalaryHistoryForm from './SalaryHistoryForm'

const Schema = Yup.object().shape({
  payGrade: Yup.string().required(),
  annualBasicPay: Yup.number().required(),
  currency: Yup.string().required(),
  earnings: Yup.string().required(),
  comment: Yup.string().required(),
  deductions: Yup.string().required(),
  from: Yup.date().required(),
  to: Yup.date().required()
})

const SalaryHistory = (props) => {
  const [toggle, setToggle] = useState(false)
  const [isAddType, setIsAddType] = useState(true)
  const { currentEmployee, salaryHistory, dispatch, values, setValues, submitForm, errors } = props

  useEffect(() => {
    if (currentEmployee?.id) {
      dispatch(getSalaryHistoryDetailsByEmployeeID(currentEmployee.id))
    }
  }, [currentEmployee?.id])

  const handleAddNewDetails = () => {
    if (currentEmployee?.id) {
      setToggle(true)
      setIsAddType(true)
      setValues({
        from: '',
        to: '',
        payGrade: '',
        annualBasicPay: '',
        currency: '',
        earnings: '',
        comment: '',
        deductions: '',
        isAddType: true
      })
    } else {
      message.error('Please select an employee to continue!')
    }
  }

  const handleEditRow = (row) => () => {
    setToggle(true)
    setValues({ ...row, isAddType: false })
    setIsAddType(false)
  }

  const handleValueChange = (val) => {
    setValues({ ...values, ...val })
  }

  const deleteRow = (row) => () => {
    dispatch(updateSalaryHistoryDetailsByEmployeeID(row.id, { status: 'Inactive' }))
  }

  const onSave = () => {
    submitForm()

    if (isEmpty(errors)) {
      setToggle(false)
    }
  }

  const columns = [
    {
      title: 'From Date',
      dataIndex: 'from',
      render: (text) => moment(text).format('DD-MMM-YYYY')
    },
    {
      title: 'To Date',
      dataIndex: 'to',
      render: (text) => moment(text).format('DD-MMM-YYYY')
    },
    {
      title: 'Pay Grade',
      dataIndex: 'payGrade'
    },
    {
      title: 'Annual Basic Payment',
      dataIndex: 'annualBasicPay'
    },
    {
      title: 'Currency',
      dataIndex: 'currency'
    },
    {
      title: 'Earnings',
      dataIndex: 'earnings'
    },
    {
      title: 'Deductions',
      dataIndex: 'deductions'
    },
    {
      title: 'Comment',
      dataIndex: 'comment'
    },
    {
      title: 'Action',
      dataIndex: 'custom_action',
      render: (text, row) => (
        <Popover placement="bottomRight" content={tableActions(row)} trigger="click">
          <div className="btn-group">
            <button type="button" className="btn glow dropdown-toggle">
              {' '}
              <SettingOutlined /> <span className="caret" />
            </button>
          </div>
        </Popover>
      )
    }
  ]

  const tableActions = (row) => (
    <div className="action-buttons">
      <ul>
        <li onClick={handleEditRow(row)}>
          <i className="flaticon-edit-1" /> Edit
        </li>
        <li>
          <Popconfirm title="Sure to delete?" onConfirm={deleteRow(row)}>
            <i className="flaticon-delete-2" /> Delete
          </Popconfirm>
        </li>
      </ul>
    </div>
  )

  return (
    <Form>
      <PanelLayout>
        <Panel title="Salary History">
          <div className="panel-with-border">
            <Row justify="left" gutter={(12, 10)}>
              <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                <div className="mb-3 align-right">
                  <ButtonBox style={{ marginRight: 10 }} type="success" onClick={handleAddNewDetails}>
                    <i className="flaticon-plus" /> Add
                  </ButtonBox>
                  {/* <ButtonBox type="primary">
                    {' '}
                    <i className="flaticon-delete-3" /> Delete{' '}
                  </ButtonBox> */}
                </div>
                <div className="table-view">
                  <TableBox
                    columns={columns}
                    actionIndex="custom_action"
                    cardHeaderIndex="status"
                    cardFirstLabelIndex="docno"
                    dataSource={salaryHistory}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </Panel>
      </PanelLayout>

      <ModalBox
        title={`${isAddType ? 'Add' : 'Edit'} Salary History Details`}
        visible={toggle}
        onCancel={() => setToggle(false)}
        width={700}
        okText="Save"
        onOk={onSave}
        destroyOnClose>
        <SalaryHistoryForm currentDetails={values} handleValueChange={handleValueChange} />
      </ModalBox>

      <FooterActions
        leftActions={[
          {
            prefix: 'flaticon-back',
            label: 'Back to employee list'
          }
        ]}
      />
    </Form>
  )
}

const mapStateToProps = (state) => ({
  salaryHistory: state.employee.employeeDetails.salaryHistory
})

export default connect(
  mapStateToProps,
  null
)(
  withFormik({
    mapPropsToValues: () => ({
      from: '',
      to: '',
      payGrade: '',
      annualBasicPay: '',
      currency: '',
      earnings: '',
      comment: '',
      deductions: ''
    }),
    handleSubmit: async (values, { props: { dispatch, currentEmployee } }) => {
      const { id, isAddType, ...rest } = values
      const payload = {
        payGrade: rest.payGrade,
        annualBasicPay: rest.annualBasicPay,
        currency: rest.currency,
        earnings: rest.earnings,
        comment: rest.comment,
        deductions: rest.deductions,
        employee: currentEmployee.id,
        from: rest.from,
        to: rest.to
      }

      if (isAddType) {
        dispatch(addSalaryHistoryDetails(payload))
      } else {
        dispatch(updateSalaryHistoryDetailsByEmployeeID(id, payload))
      }
    },
    validationSchema: Schema
  })(SalaryHistory)
)
